import React, {Fragment, useCallback, useState} from 'react';
import clsx from 'clsx';
import scrollIntoView from 'scroll-into-view-if-needed';
import {unstable_ownerDocument} from '@mui/utils';
import {type HeaderGroup, type RowData} from '@tanstack/react-table';
import {type ColumnPinningState} from '@tanstack/table-core';
import useSharedRef from '@netx/core-components/hooks/useSharedRef';
import {composeClasses, styled, WithClasses} from '@netx/core-components/Theme';
import {TableUtilityCellType} from '../constants';
import {TableHeaderRowCell} from './TableHeaderRowCell';
import {
	getTableHeaderRowUtilityClass,
	TABLE_HEADER_ROW_CLASS_NAME,
	tableHeaderRowClasses,
	type TableHeaderRowClasses,
} from './tableHeaderRowClasses';
import {TableRow, type TableRowProps} from './TableRow';

const TABLE_UTILITY_CELL_TYPES = Object.values(TableUtilityCellType);

type TableHeaderRowOwnerState = WithClasses<
	TableHeaderRowClasses,
	{
		disabled?: boolean;
	}
>;

const useUtilityClasses = (ownerState: TableHeaderRowOwnerState) => {
	const {classes, disabled} = ownerState;
	const slots = {
		root: ['root', disabled && 'disabled'],
	};
	return composeClasses(slots, getTableHeaderRowUtilityClass, classes);
};

const TableHeaderRowRoot = styled(TableRow, {
	name: TABLE_HEADER_ROW_CLASS_NAME,
	slot: 'Root',
})(({theme, ownerState}) => ({
	...(ownerState.disabled && {
		pointerEvents: 'none',
		opacity: 0.8,
	}),
}));

const TableHeaderRowDummyCell = styled('div')(({theme}) => ({
	flex: '1 1 auto',
}));

const moveFocus = (
	list: HTMLDivElement,
	currentFocus: Element | null,
	traversalFunction: any,
) => {
	let wrappedOnce = false;
	let nextFocus = traversalFunction(list, currentFocus);

	while (nextFocus) {
		// Prevent infinite loop
		if (nextFocus === list.firstChild) {
			if (wrappedOnce) return;
			wrappedOnce = true;
		}

		const nextFocusDisabled =
			nextFocus.disabled || nextFocus.getAttribute('aria-disabled') === 'true';

		const _next = nextFocus.querySelectorAll('[tabindex]')[0] || nextFocus;

		if (!_next.hasAttribute('tabIndex') || nextFocusDisabled) {
			// Move to the next element
			nextFocus = traversalFunction(list, nextFocus);
		} else {
			_next.focus({preventScroll: true});
			scrollIntoView(_next, {
				// use `nearest` so we do not attempt to scroll vertically to center the headers
				block: 'nearest',
				inline:
					nextFocus.getAttribute('data-type') === 'numerical' ? 'end' : 'start',
			});
			return;
		}
	}
};

const nextItem = (list: HTMLDivElement, item: Element) => {
	if (list === item) return list.firstChild;
	if (item?.nextElementSibling) return item.nextElementSibling;
	return list.firstChild;
};

const previousItem = (list: HTMLDivElement, item: Element) => {
	if (list === item) return list.lastChild;
	if (item?.previousElementSibling) return item.previousElementSibling;
	return list.lastChild;
};

export interface TableHeaderRowProps<TData extends RowData>
	extends WithClasses<TableHeaderRowClasses, TableRowProps<TData>> {
	disableFocus?: boolean;
	disabled?: boolean;
	headerGroup: HeaderGroup<TData>;
	height?: number;
	lastSizeableColumnIndex?: number;
	leftLeafColumnWidths?: number[];
	pinnedColumns?: ColumnPinningState;
	rightLeafColumnWidths?: number[];
}

export const TableHeaderRowPrimitive = <TData extends RowData>(
	{
		classes: _classes,
		className,
		disableFocus,
		disabled,
		headerGroup,
		height,
		lastSizeableColumnIndex,
		leftLeafColumnWidths,
		pinnedColumns,
		rightLeafColumnWidths,
		...props
	}: TableHeaderRowProps<TData>,
	ref: React.ForwardedRef<HTMLDivElement>,
) => {
	const [selected, setSelected] = useState(-1);
	const columnsWrapperRef = useSharedRef<HTMLDivElement | null>(null, [ref]);

	const ownerState: TableHeaderRowOwnerState = {
		classes: _classes,
		className,
		disabled,
	};

	const classes = useUtilityClasses(ownerState);

	const handleKeyDown = useCallback(
		(column: any) => (event: React.KeyboardEvent<HTMLDivElement>) => {
			const list = columnsWrapperRef.current;
			if (!list) return;

			const currentFocus = unstable_ownerDocument(list).activeElement;
			if (!currentFocus) return;

			const columnFocused: Element | null = ['cell', 'columnHeader'].includes(
				currentFocus.getAttribute('roll') as string,
			)
				? currentFocus
				: currentFocus.closest('[role="columnheader"]');

			if (!columnFocused) return;

			switch (event.key) {
				case 'ArrowLeft':
					event.preventDefault();
					moveFocus(list, columnFocused, previousItem);
					break;

				case 'ArrowRight':
					event.preventDefault();
					moveFocus(list, columnFocused, nextItem);
					break;

				case 'Home':
					event.preventDefault();
					moveFocus(list, null, nextItem);
					break;

				case 'End':
					event.preventDefault();
					moveFocus(list, null, previousItem);
					break;

				default:
					break;
			}
		},
		[],
	);

	const handleColumnFocusCapture = useCallback(
		(column: any, index: number) =>
			(event: React.FocusEvent<HTMLDivElement>) => {
				if (
					disableFocus ||
					(!column.getCanSort() &&
						(column.columnDef.meta as any)?.isInteractive !== true)
				) {
					event.target.blur();
					return;
				}
				setSelected(index);
			},
		[disableFocus],
	);

	const firstActiveIndex = headerGroup.headers.findIndex(
		(header) =>
			header.column.getCanSort() ||
			(header.column.columnDef.meta as any)?.isInteractive === true,
	);

	return (
		<TableHeaderRowRoot
			ref={columnsWrapperRef}
			className={clsx(classes.root, className)}
			role="row"
			{...props}
			ownerState={ownerState}
		>
			{headerGroup.headers.map((header, index) => {
				return (
					<Fragment key={header.id}>
						{lastSizeableColumnIndex === index && (
							<TableHeaderRowDummyCell aria-hidden="true" />
						)}
						<TableHeaderRowCell
							disabled={disabled}
							header={header}
							height={height}
							index={index}
							isDataCell={
								!TABLE_UTILITY_CELL_TYPES.includes(
									header.id as typeof TABLE_UTILITY_CELL_TYPES[number],
								)
							}
							leftLeafColumnWidths={leftLeafColumnWidths}
							pinnedColumns={pinnedColumns}
							rightLeafColumnWidths={rightLeafColumnWidths}
							onFocusCapture={handleColumnFocusCapture(header.column, index)}
							onKeyDownCapture={handleKeyDown(header.column)}
							tabIndex={
								!disableFocus &&
								(selected === index ||
									(selected < 0 && index === firstActiveIndex))
									? 0
									: -1
							}
						/>
					</Fragment>
				);
			})}
		</TableHeaderRowRoot>
	);
};

export const TableHeaderRow = React.forwardRef(TableHeaderRowPrimitive) as <
	TData extends RowData,
>(
	props: TableHeaderRowProps<TData>,
) => JSX.Element;

export default TableHeaderRow;
